import axios from 'axios';
import Cookies from 'universal-cookie';
import { setSignInState } from '../actions/private-route-actions';
import { getLocalStorage, setCookie, setSessionStorage } from '../components/utilities/functions';
import flagsmith from 'flagsmith/isomorphic';
import { jwtDecode } from 'jwt-decode';
import { eloquaTrackingUrl } from '../components/utilities/constants';

const qs = require('qs');
const cookies = new Cookies();
const pellaAPIURL = process.env.GATSBY_PELLA_API;
const userPostalCode = getLocalStorage('UserPostalCode');

/* **************************************
NOTE: This entire file is not redux
************************************** */
const axiosSuccessResponse = (response) => {
	if (response?.data?.error || response?.data?.success === false) {
		response.data = {
			...response.data,
			REQUEST_ID: response.headers?.['x-pella-request-id'],
		};
	}
	return response;
};

const axiosNonAuthSuccessResponse = (error) => error;

const axiosErrorResponse = async (error) => {
	const isNetworkError = error.message && error.message.toLowerCase() === 'network error' ? true : false;
	let errorObject = null;
	if (isNetworkError) {
		errorObject = {
			response: {
				status: isNetworkError ? 401 : 400,
				data: error.message,
			},
		};
	} else {
		errorObject = error;
	}
	const originalRequest = error.config;
	if (errorObject.response.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;
		try {
			const tokenInfo = await refresh();
			if (tokenInfo && tokenInfo.success) {
				return interceptorInstance(originalRequest);
			} else {
				return errorObject;
			}
		} catch (err) {
			return errorObject;
		}
	} else {
		return errorObject;
	}
};

const interceptorInstance = axios.create();
interceptorInstance.interceptors.response.use(axiosSuccessResponse, axiosErrorResponse);
const interceptorInstanceNonAuth = axios.create();
interceptorInstanceNonAuth.interceptors.response.use(axiosSuccessResponse, axiosNonAuthSuccessResponse);

const login = (loginInfo) =>
	new Promise(async (resolve, reject) => {
		try {
			loginInfo.scopes = ['ApiGw.account', 'ApiGw.web-project', 'ApiGw.web-product', 'ApiGw.web-customer', 'ApiGw.web-cart-order-mgmt', 'ApiGw.offline'];
			const config = {
				withCredentials: true,
			};
			const tokenInfo = await interceptorInstanceNonAuth.post(`${pellaAPIURL}/account/v1.1/auth/login`, loginInfo, config);
			if (tokenInfo.data.success && !tokenInfo.data.code) {
				const customerType = await getCustomerType(tokenInfo.data);
				if (customerType.CustomerType === 'professional') {
					const date = new Date();
					setCookie('expire_modal', date.setDate(date.getDate() + 30), 3600 * 24 * 365);
				}
				try {
					let customer = await fetchCustomer();
					if (customer.statusCode === 404) {
						const user = await getUser();
						if (user && user.success) {
							customer = await generateCustomer(user.User.name.firstname, user.User.name.lastname);
							if (!customer || !customer.Customer || !customer.Customer.id) {
								throw Error('The customer record was not created in the cart-order-mgmt API');
							}
						} else {
							throw Error('There was an error retrieving the account details of the account');
						}
					} else if (!customer.Customer) {
						throw Error('There was an error retrieving thse customer record in the cart-order-mgmt API');
					}

					if (customerType.CustomerType && customer.Customer.email) {
						try {
							flagsmith.identify(customer.Customer.email, { name: customer.Customer.name, type: customerType.CustomerType });
						} catch (err) {}
					}

					const guid = cookies.get('ELOQUA');
					const guidValue = guid != null ? guid.split('=').pop() : '';

					if (customerType.CustomerType && loginInfo.userName && customer.Customer.email && guidValue) {
						try {
							const config = {
								headers: {
									'Content-Type': 'application/x-www-form-urlencoded',
								},
							};

							const body = {
								elqFormName: 'login',
								elqSiteID: '1920192983',
								elqCustomerGUID: guidValue,
								elqCookieWrite: '0',
								email: customer.Customer.email,
								username: loginInfo.userName,
								customerType: customerType.CustomerType,
								env: process.env.GATSBY_CONTENTSTACK_ENVIRONMENT,
							};
							const eloquaStatus = await axios.post(eloquaTrackingUrl, qs.stringify(body), config);
						} catch (ex) {}
					}
				} catch (ex) {}
				saveTokens(tokenInfo.data, customerType);
				const user = jwtDecode(tokenInfo.data.Token.access_token);
				setSessionStorage('authSession', {
					isPro: customerType.CustomerType === 'professional' ? true : false,
					token: tokenInfo.data.Token.access_token,
					email: user.mail,
					firstName: user.firstname,
				});
				resolve({
					success: true,
					isPro: customerType.CustomerType === 'professional' ? true : false,
					token: tokenInfo.data.Token.access_token,
					email: user.mail,
					firstName: user.firstname,
				});
			} else {
				setSessionStorage('authSession', {
					isPro: false,
				});
				resolve(tokenInfo.data);
			}
		} catch (error) {
			setSessionStorage('authSession', {
				isPro: false,
			});
			if (error.response) {
				resolve(error.response.data);
			} else {
				resolve({ success: false });
			}
		}
	});

const refresh = () =>
	new Promise(async (resolve) => {
		try {
			const config = {
				withCredentials: true,
			};
			const tokenInfo = await interceptorInstance.post(`${pellaAPIURL}/account/v1.1/auth/refresh`, {}, config);
			setSessionStorage('authSession', {
				isPro: false,
			});
			resolve(tokenInfo.data);
		} catch (error) {
			setSessionStorage('authSession', {
				isPro: false,
			});
			if (error.response && error.response.data) {
				resolve(error.response.data);
			} else {
				resolve({ success: false });
			}
		}
	});

const getCustomerType = (tokenInfo) =>
	new Promise(async (resolve) => {
		try {
			const config = {
				headers: {
					Authorization: `Bearer ${tokenInfo.Token.access_token}`,
				},
			};
			const customerType = await interceptorInstance.get(`${pellaAPIURL}/account/v1.1/user-type`, config);
			resolve(customerType.data);
		} catch (error) {
			if (error.response && error.response.data) {
				resolve(error.response.data);
			} else {
				resolve({ success: false });
			}
		}
	});

// Helper functions
const saveTokens = (tokenInfo, customerType) => {
	setSignInState(tokenInfo.Token.access_token, customerType);
};

// Needed to move these calls from cart-order-actions to remove cyclical nature
const generateCustomer = (firstname, lastname) =>
	new Promise(async (resolve) => {
		try {
			let params = {};
			const config = {
				withCredentials: true,
			};

			const body = {
				firstname: firstname,
				lastname: lastname,
			};

			if (userPostalCode) {
				params.client_postal_code = userPostalCode;
			}

			const customer = await interceptorInstance.post(`${pellaAPIURL}/web-cart-order-mgmt/v1/customers`, body, { ...config, params: params });
			resolve(customer.data);
		} catch (error) {
			if (error.response && error.response.data) {
				resolve(error.response.data);
			} else {
				resolve({ success: false });
			}
		}
	});

export { login, refresh, getCustomerType, axiosSuccessResponse, axiosNonAuthSuccessResponse, axiosErrorResponse, generateCustomer };
